import {
  computeYoYChange,
  getCurrentAndPreviousYearData,
} from '@react/data-hub/helpers/calculateGrowthRate';
import round from '@react/lib/helpers/round';
import { formatMoney } from '@sly/core/helpers/numbers';
import { stateAbbr } from '@sly/core/src/constants/geo';
import { SearchInsights, YearlyTrends } from '@sly/core/src/types';

export function getFirstAndLastYear(
  yearlyTrends: YearlyTrends | null | undefined
) {
  if (!yearlyTrends) return { earliestYear: '', latestYear: '' };
  const years = Object.keys(yearlyTrends);
  const latestYear = years.sort()?.[years.length - 1];
  const earliestYear = years.sort()?.[0];

  return { earliestYear, latestYear };
}

export function generateSummaryParagraph(data: SearchInsights) {
  const { careType, info, cityAveragePricing } = data;

  const yearlyTrends = info?.yearlyTrends;
  if (!yearlyTrends) return '';

  const growthRate = computeYoYChange(yearlyTrends);
  const trend = growthRate >= 0 ? 'risen' : 'fallen';
  const locationString = getLocationString(data);

  const { currentYearData, lastYearData } =
    getCurrentAndPreviousYearData(yearlyTrends);

  const cityOrNational = locationString ? `in ${locationString}` : 'nationally';
  const increaseOrDecrease =
    currentYearData >= lastYearData ? 'increase' : 'decrease';

  return `${careType} prices have ${trend} an average of ${Math.abs(
    growthRate
  )}% a year since last year. Below is the median cost each year and its percent ${increaseOrDecrease}, according to our proprietary data. On average, ${careType.toLowerCase()} ${cityOrNational} is ${formatMoney(
    cityAveragePricing
  )}/mo.`;
}

export function generateLineChartHeading(
  data: SearchInsights & { roomType: string }
) {
  const { careType, info, roomType } = data;

  const locationString = getLocationString(data);

  const yearlyTrends = info?.yearlyTrends;

  if (!(yearlyTrends instanceof Object) && !yearlyTrends) return '';

  const { latestYear } = getFirstAndLastYear(yearlyTrends);

  const heading = `Montly median cost for ${careType.toLowerCase()}, ${roomType.toLowerCase()}: ${locationString} (${latestYear})`;
  return heading;
}

export function getGrowthRateText(data: YearlyTrends) {
  if (!data) return '';
  const growthRate = computeYoYChange(data);

  if (growthRate === 0) {
    return '';
  }

  const prefix = growthRate >= 0 ? '+' : '';
  return `${prefix}${growthRate}% year over year`;
}

type LocationType = {
  type: string;
  stateName: string;
  name: string;
};

export function addCountryCode(locationString: string) {
  if (!locationString) return '';
  return `${locationString}, USA`;
}

export function getLocationString(data: LocationType): string {
  const { type, stateName, name } = data;
  if (!stateName || !name) return '';
  const abbrStateName = stateAbbr[stateName as keyof typeof stateAbbr];
  let location = '';
  if (type === 'state') {
    location = `${stateName}`;
  } else if (type === 'city') {
    location = `${name}, ${abbrStateName}`;
  } else if (type === 'county') {
    location = `${name} County, ${abbrStateName}`;
  }
  return location;
}

interface LicensingParagraphData {
  cityName: string;
  noOfIssuesForCity: number;
  noOfIssuesForCounty: number;
  noOfIssuesForState: number;
  countyName: string;
  stateName: string;
  careType: string;
  currentYear: number;
  issueType: 'citation' | 'inspection' | 'allegation';
}

export function getDifferenceInString(num: number) {
  return num > 0 ? 'higher' : num < 0 ? 'lower' : 'same as';
}

export function getDifferenceSentence(num: number) {
  return num !== 0 ? String(round(Math.abs(num), 2)) + '%' : '';
}

export function generateLicensingParagraph(data: LicensingParagraphData) {
  const {
    cityName,
    noOfIssuesForCity,
    noOfIssuesForCounty,
    noOfIssuesForState,
    countyName,
    stateName,
    careType,
    currentYear,
    issueType,
  } = data;

  const countyDifference =
    noOfIssuesForCounty !== 0
      ? (noOfIssuesForCity / noOfIssuesForCounty - 1) * 100
      : 0;

  const countyDifferenceStr =
    noOfIssuesForCounty !== 0
      ? `This is ${getDifferenceSentence(
          countyDifference
        )} ${getDifferenceInString(countyDifference)} ${
          countyDifference !== 0 ? 'than' : ''
        } the ${countyName} County average of ${round(
          noOfIssuesForCounty,
          2
        )} ${issueType}s and `
      : '';

  const stateDifference =
    noOfIssuesForState !== 0
      ? (noOfIssuesForCity / noOfIssuesForState - 1) * 100
      : 0;

  const stateDifferenceStr =
    noOfIssuesForState !== 0
      ? `${getDifferenceSentence(stateDifference)} ${getDifferenceInString(
          stateDifference
        )} ${
          stateDifference !== 0 ? 'than' : ''
        } the ${stateName} average of ${round(
          noOfIssuesForState,
          2
        )} ${issueType}s.`
      : '';

  const inspectionNote =
    issueType === 'inspection'
      ? `A lower number of inspections in ${careType} facilities is actually better because it indicates a higher level of consistent compliance with regulations and standards.`
      : '';

  return `${cityName} ${careType} communities had an average of ${round(
    noOfIssuesForCity,
    2
  )} ${issueType}s in ${currentYear}. ${countyDifferenceStr}${stateDifferenceStr} ${inspectionNote}`;
}
